import { SharedBasePaymentMethods } from 'src/app/_shared/classes/settlements/shared-base-payment-methods';
import {
    SharedPaymentMethodGetCollectifAccountWithInvoicesIdsListAction,
    SharedPaymentMethodGetPaymentMethodsFromInvoicesIdsListAction,
    SharedPaymentMethodsGetBankAccountWithInvoicesIdsListAction,
    SharedPaymentMethodsInitPaymentMethodsAction
} from 'src/app/_shared/stores/settlements/shared-payment-methods/shared-payment-methods.action';
import {
    CriteresBenefDtoInput,
    CriteresFournisseurDtoInput,
    CriteresMoyenPaiementDtoInput
} from 'src/graphql/generated-types';
import { forkJoin, Observable } from 'rxjs';
import { TypeReglement } from 'src/app/board/activity/activity.constants';
import { Store } from '@ngxs/store';
import { take, tap } from 'rxjs/operators';

export class SharedAdvancedSettlement extends SharedBasePaymentMethods {

    accountingEntryLabel: string;

    thirdPartyId: number;

    invoiceNumber: string;

    constructor(protected store: Store,
                activityId: number,
                private paymentType: TypeReglement,
                private benefOrProviderId: number) {
        super(store, activityId);

        this.thirdPartyId = this.benefOrProviderId;
    }

    private get beneficiaryOrProviderCriterias(): CriteresBenefDtoInput | CriteresFournisseurDtoInput {
        const criterias: CriteresBenefDtoInput = {};

        if (this.isClientContext || this.isClientDeposit) {
            criterias.TakeRibEtMandat = true;
            criterias.ListeIdBenef = this.thirdPartyId ? '' + this.thirdPartyId : null;
        } else if (this.isProviderContext) {
            criterias.Id = this.thirdPartyId;
            criterias.TakeRibs = true;
        }

        return criterias;
    }

    private get paymentCriterias(): CriteresMoyenPaiementDtoInput {
        const criterias: CriteresMoyenPaiementDtoInput = {
            IdActivite: this.activityId,
            IsClos: false,
            TakeCommission: true,
            TakeArticleWithStock: true,
            IsVenteInActivite: this.isPaymentCredit,
            IsAchatInActivite: this.isPaymentDebit
        };

        return criterias;
    }

    get isProviderContext(): boolean {
        return [
            TypeReglement.Fournisseur,
            TypeReglement.RemboursementAvoirFournisseur,
            TypeReglement.AcompteFournisseur,
        ].some(e => e === this.paymentType);
    }

    get isClientContext(): boolean {
        return [
            TypeReglement.DonCEBenef,
            TypeReglement.ReglementPretBenef,
            TypeReglement.Beneficiaire,
            TypeReglement.RemboursementAvoirClient,
            TypeReglement.RemboursementPretBenef,
        ].some(e => e === this.paymentType);
    }

    get isClientDeposit(): boolean {
        return this.paymentType === TypeReglement.AcompteClient;
    }

    get isRefundContext(): boolean {
        return [
            TypeReglement.ReglementPretBenef,
            TypeReglement.RemboursementAvoirClient,
            TypeReglement.RemboursementAvoirFournisseur
        ].some(e => e === this.paymentType);
    }

    private get isPaymentDebit(): boolean {
        return [
            TypeReglement.RemboursementAvoirClient,
            TypeReglement.Fournisseur
        ].some(e => e === this.paymentType);
    }

    private get isPaymentCredit(): boolean {
        return [
            TypeReglement.Beneficiaire,
            TypeReglement.RemboursementAvoirFournisseur,
            TypeReglement.RemboursementPretBenef,
        ].some(e => e === this.paymentType);
    }

    private get entryLabel(): string {
        let label = '';

        switch (this.paymentType) {
            case TypeReglement.ReglementPretBenef:
            case TypeReglement.RemboursementAvoirClient:
            case TypeReglement.RemboursementAvoirFournisseur:
                label = 'Remb. Avr ';
                break;
            case TypeReglement.AcompteClient:
            case TypeReglement.AcompteFournisseur:
                label = 'Remb. Acompte ';
                break;
            default:
                label = 'Rglt. Fac ';
                break;
        }

        label += (this.isClientContext || this.isClientDeposit ? 'client' : 'fournisseur');

        if (!!this.invoiceNumber) {
            label += ` n°${this.invoiceNumber}`;
        }

        if (this.third) {
            label += ` - ${this.third.Nom}`;
            if (this.third.__typename === 'BenefDTO') {
                label += ` ${this.third.Prenom}`;
            }
        }

        return label;
    }

    changeInvoicesList$(invoicesIdList: number[]): Observable<any> {
        return forkJoin([
            this.store.dispatch(new SharedPaymentMethodGetCollectifAccountWithInvoicesIdsListAction(invoicesIdList)),
            this.store.dispatch(new SharedPaymentMethodsGetBankAccountWithInvoicesIdsListAction(invoicesIdList)),
            this.store.dispatch(new SharedPaymentMethodGetPaymentMethodsFromInvoicesIdsListAction(
                invoicesIdList,
                this.isPaymentCredit ? 'credit' : 'debit')
            )
        ]);
    }

    initPaymentMethods(): Observable<any> {
        return this.store.dispatch(new SharedPaymentMethodsInitPaymentMethodsAction({
            paymentType: this.paymentType,
            beneficiaryCriterias: this.isProviderContext ? null : this.beneficiaryOrProviderCriterias,
            supplierCriterias: this.isProviderContext ? this.beneficiaryOrProviderCriterias : null,
            paymentCriterias: this.paymentCriterias
        })).pipe(take(1),
            tap(() => {
                this.accountingEntryLabel = this.entryLabel;
                this.isInitialized.next(true);
            }),
        );
    }

}
